/**
 * Lifted shipping countdown — custom element.
 * Reads the cutoff time (data-cutoff, e.g. "9:00 AM") + timezone from data attrs.
 * Updates every minute.
 */
(() => {
  if (customElements.get('lifted-shipping-countdown')) return;

  // Parse a cutoff label ("9:00 AM", "9:30 AM", "10am", "14:30") → minutes from midnight.
  function parseCutoff(s) {
    s = (s == null ? '' : ('' + s)).trim().toLowerCase();
    const m = s.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/);
    if (!m) return 540; // 9:00 fallback
    let h = parseInt(m[1], 10);
    const min = m[2] ? parseInt(m[2], 10) : 0;
    const ap = m[3];
    if (ap === 'pm' && h < 12) h += 12;
    if (ap === 'am' && h === 12) h = 0;
    return (h * 60 + min) % 1440;
  }

  class LiftedShippingCountdown extends HTMLElement {
    connectedCallback() {
      this.cutoffMin = parseCutoff(this.dataset.cutoff || '9:00 AM');
      this.tz = this.dataset.timezone || 'Europe/Warsaw';
      this.locale = document.documentElement.lang || 'en';
      // i18n templates from data-attrs (brace-free __TIME__/__DAY__ sentinels);
      // weekday names are localized via Intl from the page locale.
      this.i18n = {
        orderIn: this.dataset.i18nOrderIn || 'Order in __TIME__ · ships __DAY__',
        ships: this.dataset.i18nShips || 'Ships __DAY__',
        today: this.dataset.i18nToday || 'today',
        tomorrow: this.dataset.i18nTomorrow || 'tomorrow',
      };
      this.textEls = this.querySelectorAll('[data-ship-text]');
      this.update();
      this.interval = setInterval(() => this.update(), 60_000);
    }
    disconnectedCallback() {
      if (this.interval) clearInterval(this.interval);
    }
    nowInTz() {
      const fmt = new Intl.DateTimeFormat('en-GB', {
        timeZone: this.tz,
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
      const parts = fmt.formatToParts(new Date());
      const get = (t) => parts.find((p) => p.type === t)?.value || '';
      const day = get('weekday');
      const h = parseInt(get('hour'), 10);
      const m = parseInt(get('minute'), 10);
      const dayMap = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 0 };
      return { dayName: day, dayNum: dayMap[day], hour: h, minute: m };
    }
    computeCountdown() {
      const { dayNum, hour, minute } = this.nowInTz();
      const beforeCutoffToday = (hour * 60 + minute) < this.cutoffMin;
      const isWeekday = dayNum >= 1 && dayNum <= 5;
      let shipsOn;
      let targetDayOffset;
      if (isWeekday && beforeCutoffToday) {
        shipsOn = 'today';
        targetDayOffset = 0;
      } else {
        let offset = 1;
        let nextDay = (dayNum + offset) % 7;
        while (nextDay === 0 || nextDay === 6) { offset += 1; nextDay = (dayNum + offset) % 7; }
        if (isWeekday && !beforeCutoffToday && dayNum >= 1 && dayNum <= 4) {
          shipsOn = 'tomorrow';
        } else {
          const names = { 1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday', 5: 'Friday' };
          shipsOn = names[nextDay] || 'Monday';
        }
        targetDayOffset = offset;
      }
      const minutesNow = hour * 60 + minute;
      const cutoffMin = this.cutoffMin;
      const minutesLeftToday = cutoffMin - minutesNow;
      let minutesLeft;
      if (targetDayOffset === 0) {
        minutesLeft = minutesLeftToday;
      } else {
        const remainingToday = (24 * 60) - minutesNow;
        const fullDaysBetween = (targetDayOffset - 1) * 24 * 60;
        minutesLeft = remainingToday + fullDaysBetween + cutoffMin;
      }
      return { minutesLeft: Math.max(0, minutesLeft), shipsOn, targetDayOffset };
    }
    formatLeft(minutesLeft) {
      if (minutesLeft <= 0) return null;
      const h = Math.floor(minutesLeft / 60);
      const m = minutesLeft % 60;
      if (h === 0) return `${m}m`;
      if (h < 24) return `${h}h ${m}m`;
      const d = Math.floor(h / 24);
      const remH = h % 24;
      return `${d}d ${remH}h`;
    }
    // shipsOn is an internal code ('today'/'tomorrow'/English weekday). Map it
    // to a localized label: today/tomorrow from the t-keys, weekday via Intl.
    dayLabel(shipsOn, offset) {
      if (shipsOn === 'today') return this.i18n.today;
      if (shipsOn === 'tomorrow') return this.i18n.tomorrow;
      try {
        const d = new Date(Date.now() + (offset || 0) * 86400000);
        return new Intl.DateTimeFormat(this.locale, { weekday: 'long', timeZone: this.tz }).format(d);
      } catch (_) { return shipsOn; }
    }
    tmpl(s, vars) {
      s = (s == null ? '' : '' + s);
      for (const k in vars) s = s.split('__' + k.toUpperCase() + '__').join(vars[k]);
      return s;
    }
    update() {
      if (!this.textEls || this.textEls.length === 0) return;
      const { minutesLeft, shipsOn, targetDayOffset } = this.computeCountdown();
      const left = this.formatLeft(minutesLeft);
      let text;
      if (!left) {
        // Cutoff passed for the target → next ship day (tomorrow if target was today).
        const day = (shipsOn === 'today') ? this.i18n.tomorrow : this.dayLabel(shipsOn, targetDayOffset);
        text = this.tmpl(this.i18n.ships, { day });
      } else {
        text = this.tmpl(this.i18n.orderIn, { time: left, day: this.dayLabel(shipsOn, targetDayOffset) });
      }
      this.textEls.forEach(el => { el.textContent = text; });
    }
  }
  customElements.define('lifted-shipping-countdown', LiftedShippingCountdown);
})();
